import { ApiError, get, patch, post, type Lead, type Me } from '../api.js';
import {
  STATUS_LABEL, ago, button, chip, clear, drawer, el, empty, field, fmtHour, fmtMoney,
  fmtPhone, input, muted, panel, ring, row, select, skeleton, table, toast,
} from '../ui.js';
import {
  adviseDraftDay, dobDayOfMonth, BENEFIT_SCHEDULES, BENEFIT_SCHEDULE_PROMPT,
} from '../../../../packages/core/src/benefits.js';
import { STATES } from '../../../../packages/core/src/states.js';

interface LeadPage { items: Lead[]; total: number; limit: number; offset: number }

const SORTS: Array<[string, string]> = [
  ['score', 'Highest score first'],
  ['newest', 'Newest first'],
  ['oldest', 'Oldest first'],
  ['attempts', 'Most attempts'],
  ['name', 'Name'],
];

/**
 * `openId` deep-links to one lead.
 *
 * Added because the confirm queue on the dialling floor sends an agent to a
 * specific person, and until this existed `/leads/<id>` quietly landed on the
 * list — a button that looked like it worked and went nowhere. A lead is now
 * addressable, which is also what makes it shareable between two people who
 * are talking about the same client.
 */
export function leadsView(me: Me, go: (p: string) => void, openId?: string): HTMLElement {
  const host = el('div');
  const state = { q: '', status: '', sort: 'score', minScore: '', offset: 0 };

  /*
   * Who a lead can be moved to. Loaded once and only for somebody who may
   * actually reassign, because everybody else would be fetching a staff list
   * to render a piece of text.
   *
   * A failed load leaves it empty and the control simply does not appear —
   * better than a picker with nobody in it, which reads as "there is nobody
   * here" rather than "this did not load".
   */
  const people: Array<{ id: string; name: string }> = [];
  if (me.capabilities.includes('lead.assign')) {
    void get<Array<{ id: string; firstName: string; lastName: string; status: string }>>('/v1/users')
      .then((rows) => {
        for (const u of rows) {
          if (u.status !== 'active') continue;
          people.push({ id: u.id, name: `${u.firstName} ${u.lastName}`.trim() || 'Unnamed' });
        }
        people.sort((a, b) => a.name.localeCompare(b.name));
      })
      .catch(() => { /* no picker rather than an empty one */ });
  }

  const results = el('div');
  const summary = el('div', { class: 'tiles', style: 'margin-bottom:14px' });

  const q = input({ type: 'search', placeholder: 'Name, phone or email', value: state.q });
  const status = select([['', 'Any status'], ...Object.entries(STATUS_LABEL).map(([v, l]) => [v, l] as [string, string])]);
  const sort = select(SORTS, state.sort);
  const minScore = input({ type: 'number', min: '0', max: '100', placeholder: 'Any score' });

  let debounce: number | undefined;
  const rerun = (resetPage = true): void => {
    if (resetPage) state.offset = 0;
    state.q = q.value.trim();
    state.status = status.value;
    state.sort = sort.value;
    state.minScore = minScore.value;
    void load();
  };
  q.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => rerun(), 220);
  });
  for (const control of [status, sort, minScore]) control.addEventListener('change', () => rerun());

  const addBtn = button('Add a lead', 'pri', () => addLeadSheet(() => { state.offset = 0; void load(); }));

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Leads' }),
    el('div', { style: 'display:flex;align-items:center;gap:14px;flex-wrap:wrap' },
      el('h1', { text: 'The book', style: 'margin:0' }),
      ...(me.capabilities.includes('lead.write.own')
        || me.capabilities.includes('lead.write.team')
        || me.capabilities.includes('lead.write.all') ? [addBtn] : [])),
    el('p', { text: me.capabilities.includes('lead.read.all')
      ? 'Every lead in the agency.'
      : me.capabilities.includes('lead.read.team')
        ? 'Every lead owned by anyone on the teams you lead, plus whatever is unassigned.'
        : 'The leads assigned to you. Nobody else can see them, and you cannot see theirs.' })));

  host.append(el('div', { class: 'filters' },
    field('Search', q),
    field('Status', status),
    field('Minimum score', minScore),
    field('Order', sort)));

  host.append(summary, results);
  void load();
  if (openId) void openLead(openId);

  async function load(): Promise<void> {
    clear(results);
    results.append(skeleton(6));
    const params = new URLSearchParams({ limit: '50', offset: String(state.offset), sort: state.sort });
    if (state.q) params.set('q', state.q);
    if (state.status) params.set('status', state.status);
    if (state.minScore) params.set('minScore', state.minScore);

    let page: LeadPage;
    try {
      page = await get<LeadPage>(`/v1/leads?${params.toString()}`);
    } catch (e) {
      clear(results);
      results.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not load leads.'));
      return;
    }

    clear(summary);
    const callable = page.items.filter((l) => l.gate.ok).length;
    const blocked = page.items.length - callable;
    summary.append(
      tileOf('Matching', page.total.toLocaleString(), '', 'var(--aqua)'),
      tileOf('Callable now', String(callable), 'on this page', 'var(--good)'),
      tileOf('Blocked', String(blocked), 'and why is on each row', blocked ? 'var(--crit)' : 'var(--ink-4)'),
      tileOf('Never dialled', String(page.items.filter((l) => l.attempts === 0).length), 'on this page', 'var(--warn)'),
    );

    clear(results);
    if (page.total === 0) {
      results.append(panel(null, emptyState()));
      return;
    }

    const list = el('div', { class: 'rows' });
    for (const lead of page.items) list.append(leadRow(lead));
    results.append(panel(null, list));

    if (page.total > page.limit) {
      const pages = Math.ceil(page.total / page.limit);
      const cur = Math.floor(page.offset / page.limit) + 1;
      results.append(el('div', { style: 'display:flex;gap:8px;align-items:center;justify-content:center;margin-top:6px' },
        button('Previous', 'sm', () => { state.offset = Math.max(0, state.offset - page.limit); void load(); }),
        el('span', { class: 'mono', style: 'font-size:12px;color:var(--ink-3)', text: `Page ${cur} of ${pages}` }),
        button('Next', 'sm', () => {
          if (state.offset + page.limit < page.total) { state.offset += page.limit; void load(); }
        })));
    }
  }

  function emptyState(): HTMLElement {
    if (state.q || state.status || state.minScore) {
      return empty('Nothing matches those filters', 'Try a broader search, or clear the filters.',
        button('Clear filters', 'sm', () => {
          q.value = ''; status.value = ''; minScore.value = '';
          rerun();
        }));
    }
    return empty(
      'No leads yet',
      me.capabilities.includes('lead.import')
        ? 'Import a CSV and they will appear here, cleaned, deduplicated and scored.'
        : 'Nothing has been assigned to you yet. Your team leader routes work from their side.',
      me.capabilities.includes('lead.import') ? button('Import a list', 'pri', () => go('/import')) : undefined,
    );
  }

  function leadRow(lead: Lead): HTMLElement {
    const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Unnamed lead';
    const where = lead.state ? `${lead.city ? `${lead.city}, ` : ''}${lead.state}` : 'No state';
    const when = lead.gate.ok
      ? chip(`callable · ${fmtHour(lead.localHour)} there`, 'good')
      : chip(gateShort(lead), 'crit');
    return row({
      lead: ring(lead.score),
      title: name,
      sub: `${fmtPhone(lead.phone)} · ${where} · ${STATUS_LABEL[lead.status] ?? lead.status} · ${lead.attempts} attempt${lead.attempts === 1 ? '' : 's'}`,
      right: [when],
      onclick: () => openLead(lead.id),
    });
  }

  function gateShort(lead: Lead): string {
    const f = lead.gate.failures[0];
    if (!f) return 'blocked';
    return ({
      scrub_stale: 'needs a scrub',
      consent_missing: 'no consent',
      consent_channel: 'consent mismatch',
      dnc_internal: 'do not call',
      calling_window: `opens in ${lead.minutesUntilOpen}m`,
      attempt_cap: 'attempt cap',
      campaign_inactive: 'campaign paused',
      lead_suppressed: 'suppressed',
      no_phone: 'no number',
      licence: 'not licensed',
    } as Record<string, string>)[f.code] ?? 'blocked';
  }

  /**
   * Move a lead to somebody else.
   *
   * A select rather than a drawer: reassignment is a single decision made while
   * looking at the record, and the routing rules are the part with teeth — the
   * server refuses an agent who is not licensed in that lead's state, and it is
   * that refusal, in the carrier's own words, that has to reach the screen. So
   * a rejected change puts the old owner back rather than leaving the box
   * showing a person who does not own this lead.
   */
  function ownerControl(lead: Lead, done: () => void): HTMLElement {
    const wrap = el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' });
    const current = lead.ownerId ?? '';
    const sel = select([['', 'Unassigned'], ...people.map((u) => [u.id, u.name] as [string, string])], current);
    sel.style.maxWidth = '220px';

    sel.addEventListener('change', async () => {
      const next = sel.value || null;
      if ((next ?? '') === current) return;
      sel.disabled = true;
      try {
        await post(`/v1/leads/${lead.id}/assign`, { userId: next });
        toast(next
          ? `Moved to ${people.find((u) => u.id === next)?.name ?? 'them'}.`
          : 'Unassigned.', 'ok');
        done();
      } catch (e) {
        // Back to the truth. A box left showing the person the server refused
        // is a screen quietly claiming something that did not happen.
        sel.value = current;
        toast(e instanceof ApiError ? e.message : 'Could not move that lead.', 'crit');
      } finally {
        sel.disabled = false;
      }
    });
    wrap.append(sel);
    return wrap;
  }

  async function openLead(id: string): Promise<void> {
    const body = el('div');
    body.append(skeleton(6));
    const close = drawer('Lead', body);
    let lead: Lead;
    try {
      lead = await get<Lead>(`/v1/leads/${id}`);
    } catch (e) {
      clear(body);
      body.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not open that lead.'));
      return;
    }
    const timeline = await get<Array<{ id: string; kind: string; summary: string; occurredAt: string }>>(`/v1/leads/${id}/timeline`)
      .catch(() => [] as Array<{ id: string; kind: string; summary: string; occurredAt: string }>);

    clear(body);
    const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Unnamed lead';

    body.append(el('div', { style: 'display:flex;gap:12px;align-items:center;margin-bottom:14px' },
      ring(lead.score),
      el('div', { style: 'min-width:0' },
        el('div', { style: 'font-family:var(--f-display);font-weight:700;font-size:19px', text: name }),
        el('div', { class: 'mono', style: 'font-size:12.5px;color:var(--ink-3)', text: fmtPhone(lead.phone) }),
        el('div', { style: 'font-size:11.5px;color:var(--ink-4)', text: `${lead.city ? `${lead.city}, ` : ''}${lead.state ?? '—'} · ${fmtHour(lead.localHour)} there` }))));

    // The gate is the first thing on the screen, because it decides everything else.
    body.append(lead.gate.ok
      ? el('div', { class: 'okbox' }, el('b', null, 'Clear to call. '),
        `It is ${fmtHour(lead.localHour)} where they live, consent is on file and the attempt cap has room.`)
      : el('div', { class: 'warnbox' },
        el('b', null, `Blocked — ${lead.gate.failures.length} check${lead.gate.failures.length === 1 ? '' : 's'} failed.`),
        el('ul', { style: 'margin:7px 0 0;padding-left:16px;list-style:disc' },
          ...lead.gate.failures.map((f) => el('li', null, el('b', { text: `${f.check}: ` }), f.message)))));

    for (const w of lead.gate.warnings) {
      body.append(el('div', { class: 'note', style: 'margin-top:8px' }, el('b', { text: `${w.check}: ` }), w.message));
    }

    const actions = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin:14px 0' });
    const callBtn = button(lead.gate.ok ? 'Log a call outcome' : 'Cannot call — blocked', lead.gate.ok ? 'pri' : '', () => dispositionSheet(lead, close));
    callBtn.disabled = !lead.gate.ok;
    actions.append(callBtn, button('Add a note', '', () => noteSheet(lead)));
    if (me.capabilities.includes('ai.use')) {
      actions.append(button('Ask what to do next', 'ghost', () => askClaude(lead, body)));
    }
    body.append(actions);

    body.append(panel('Record',
      kv('Status', STATUS_LABEL[lead.status] ?? lead.status),
      kv('Owner', me.capabilities.includes('lead.assign') && people.length
        ? ownerControl(lead, () => { close(); void openLead(id); })
        : (lead.ownerName ?? 'Unassigned')),
      kv('Email', lead.email ?? '—'),
      kv('Requested cover', fmtMoney(lead.requestedFaceCents)),
      kv('Existing cover', fmtMoney(lead.existingCoverageCents)),
      kv('Dependents', lead.dependents === null ? '—' : String(lead.dependents)),
      kv('Attempts', `${lead.attempts} (${lead.answeredCount} answered)`),
      kv('Last attempt', ago(lead.lastAttemptAt)),
      kv('Added', ago(lead.createdAt))));

    /*
     * TAKEN OFF THE DIAL LIST, AND SAYING SO IN WORDS.
     *
     * Placed at the very top of the panel — above pay, above the readings,
     * above everything — because it is the single fact that changes what an
     * agent should do with this screen. A lead that quietly stopped appearing
     * in the queue with no explanation anywhere is how an agent decides the
     * queue is broken, and an agent working around the queue is outside every
     * compliance check the queue performs.
     *
     * The wording never says "removed" or "closed". It was taken off a list,
     * it is still here, and the button beside it says so.
     */
    if (lead.retiredAt) {
      const undo = button('Put it back on the dial list', 'sm', () => {
        void (async () => {
          try {
            await post(`/v1/leads/${id}/unretire`, {});
            close();
            void openLead(id);
          } catch (e) {
            toast(e instanceof Error ? e.message : 'Could not put it back');
          }
        })();
      });
      body.append(panel('Off the dial list',
        el('p', { style: 'margin:0 0 10px', text: lead.retiredBecause ?? 'The number never worked.' }),
        el('p', {
          class: 'muted',
          style: 'margin:0 0 12px;font-size:13px',
          text: 'Nobody asked us to stop — this is our own call, and it costs nothing to undo. '
            + 'The household is still in your book and every call to it is still on record.',
        }),
        undo));
    }

    body.append(payPanel(lead));

    // What the system read off this lead's calls, and has not been told is
    // right yet. Placed above the score breakdown because it is the only thing
    // on this screen waiting on the person reading it.
    const readings = el('div');
    body.append(readings);
    void callReadings(id, readings);

    // Why this person may lawfully be called. Directly beneath the readings
    // because both answer questions about the same conversation, and above the
    // score because a score is an opinion and this is a permission.
    body.append(consentPanel(id, lead, () => { close(); void openLead(id); }));

    if (lead.scoreBreakdown.length) {
      body.append(panel(`Why this score is ${lead.score}`,
        el('div', { class: 'rows' },
          ...lead.scoreBreakdown.map((b) => row({
            title: b.label,
            right: el('span', {
              class: 'mono',
              style: `color:var(--${b.points > 0 ? 'good' : 'crit'})`,
              text: `${b.points > 0 ? '+' : ''}${b.points}`,
            }),
          })))));
    }

    const extras = Object.entries(lead.custom);
    if (extras.length) {
      body.append(panel('From the vendor file', ...extras.map(([k, v]) => kv(k, v))));
    }

    body.append(panel('History',
      timeline.length
        ? el('div', { class: 'rows' }, ...timeline.map((e) => row({
          title: e.summary,
          sub: `${e.kind} · ${ago(e.occurredAt)}`,
        })))
        : el('p', { style: 'color:var(--ink-3);font-size:12.5px', text: 'Nothing has happened yet.' })));
  }

  /**
   * When this household is paid, and therefore when a premium may be drafted.
   *
   * WHY THIS IS ON THE LEAD SCREEN AND NOT THE POLICY SCREEN
   * Because it is a question, and the only moment anybody can ask it is while
   * the client is on the phone. By the time a case exists the call is over. So
   * the answer is captured here, on the record for the person, and the policy
   * screen only reads it back.
   *
   * WHY THE OPTIONS ARE PHRASED LIKE THIS
   * "Pre-May-1997 beneficiary" is the correct term and nobody has ever said it
   * out loud. The labels here are what the client would answer if you asked
   * when their check comes, because an agent reads this list mid-sentence.
   */
  function payPanel(lead: Lead): HTMLElement {
    const day = dobDayOfMonth(lead.dob ? new Date(lead.dob) : null);
    const advice = adviseDraftDay(lead.benefitSchedule, day, null);

    // Blank first, and blank is a real state: it means nobody has asked. That
    // is different from 'other' — which means asked, and not on the SSA
    // schedule — and the difference is the whole reason this reads honestly
    // instead of guessing.
    const sel = select(
      [['', 'Not asked yet'], ...BENEFIT_SCHEDULES.map((s) => [s, BENEFIT_SCHEDULE_PROMPT[s]] as [string, string])],
      lead.benefitSchedule ?? '',
    );

    const out = el('div');
    const paint = (): void => {
      clear(out);
      const a = adviseDraftDay(
        (sel.value || null) as Lead['benefitSchedule'],
        day,
        null,
      );
      out.append(el('div', {
        class: a.verdict === 'unknown' ? 'note' : 'okbox',
        style: 'margin-top:10px',
      }, a.message));
      // Only offered where there is something to offer. A row of chips with
      // nothing in them reads as a broken control rather than a missing answer.
      if (a.safeDays.length) {
        out.append(el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap;margin-top:8px' },
          ...a.safeDays.map((d) => chip(ordinal(d), 'ok'))));
      }
    };
    paint();

    sel.addEventListener('change', () => {
      const next = sel.value || null;
      sel.disabled = true;
      void patch(`/v1/leads/${lead.id}`, { benefitSchedule: next })
        .then(() => {
          lead.benefitSchedule = next as Lead['benefitSchedule'];
          paint();
        })
        .catch((e: unknown) => {
          // Back to what the server believes. A picker showing an answer that
          // was never saved is the screen telling a lie the agent will act on.
          sel.value = lead.benefitSchedule ?? '';
          toast(e instanceof ApiError ? e.message : 'Could not save that.', 'crit');
        })
        .finally(() => { sel.disabled = false; });
    });

    return panel('When their money arrives',
      el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 10px',
        text: day === null
          ? 'Ask when their check arrives each month. Their date of birth is not on file, '
            + 'and for Social Security the payment date is set by it.'
          : 'Ask: when does your check come each month?',
      }),
      sel,
      out,
      advice.arrival
        ? el('p', { style: 'font-size:11.5px;color:var(--ink-4);margin:10px 0 0' },
          'Set the premium draft for one of those days on any policy written for them. ',
          'A draft taken before the deposit lands is the single most common way a '
          + 'policy this agency wrote correctly still lapses.')
        : null);
  }

  function ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
  }

  /** A label and its value. The value may be a control, not only text. */
  function kv(k: string, v: string | HTMLElement): HTMLElement {
    return el('div', { style: 'display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-bottom:1px solid var(--rim);font-size:12.5px;align-items:center' },
      el('span', { style: 'color:var(--ink-4)', text: k }),
      typeof v === 'string'
        ? el('span', { class: 'mono', style: 'text-align:right', text: v })
        : v);
  }

  function dispositionSheet(lead: Lead, closeParent: () => void): void {
    const options: Array<[string, string]> = [
      ['answered', 'Answered — spoke to them'],
      ['no_answer', 'No answer'],
      ['voicemail', 'Left a voicemail'],
      ['busy', 'Busy'],
      ['disconnected', 'Number disconnected'],
      ['wrong_number', 'Wrong number'],
      ['callback', 'They asked for a callback'],
      ['qualified', 'Qualified'],
      ['unqualified', 'Not a fit'],
      ['appointment', 'Booked an appointment'],
      ['not_interested', 'Not interested'],
      ['do_not_contact', 'Asked not to be contacted again'],
    ];
    const pick = select(options, 'answered');
    const note = el('textarea', { class: 'inp', rows: '3', placeholder: 'What happened? (optional)' });
    const save = button('Save outcome', 'pri');
    const body = el('div', null,
      el('p', { style: 'color:var(--ink-3);font-size:12.5px;margin-bottom:12px', text: 'The lead\'s status follows from the outcome — you do not set it separately.' }),
      field('Outcome', pick),
      field('Note', note),
      save);
    const close = drawer('Call outcome', body);
    save.addEventListener('click', async () => {
      save.disabled = true;
      try {
        await post(`/v1/leads/${lead.id}/disposition`, { disposition: pick.value, note: note.value || undefined });
        toast('Outcome saved.', 'ok');
        close();
        closeParent();
        void load();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not save that outcome.', 'crit');
        save.disabled = false;
      }
    });
  }

  function noteSheet(lead: Lead): void {
    const text = el('textarea', { class: 'inp', rows: '5', placeholder: 'What should the next person to open this know?' });
    const save = button('Save note', 'pri');
    const close = drawer('Add a note', el('div', null, field('Note', text), save));
    save.addEventListener('click', async () => {
      if (!text.value.trim()) return;
      save.disabled = true;
      try {
        await post(`/v1/leads/${lead.id}/notes`, { text: text.value.trim() });
        toast('Note saved.', 'ok');
        close();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not save.', 'crit'); save.disabled = false; }
    });
  }

  async function askClaude(lead: Lead, body: HTMLElement): Promise<void> {
    const box = el('div', { class: 'note', style: 'margin-bottom:14px' }, 'Thinking…');
    body.prepend(box);
    try {
      const res = await post<{ text: string }>('/v1/ai/lead.next_action', {
        prompt: 'What is the single next action for this lead?',
        leadId: lead.id,
      });
      clear(box);
      box.append(el('b', null, 'Suggestion. '), el('span', { style: 'white-space:pre-wrap', text: res.text }));
    } catch (e) {
      clear(box);
      box.className = 'warnbox';
      box.textContent = e instanceof ApiError ? e.message : 'The assistant is not answering.';
    }
  }

  function tileOf(k: string, v: string, d: string, accent: string): HTMLElement {
    return el('div', { class: 'tile', style: `--accent:${accent}` },
      el('div', { class: 'k', text: k }),
      el('div', { class: 'v', text: v }),
      d ? el('div', { class: 'd', text: d }) : null);
  }

  void table;
  return host;
}

/* ------------------------------------------------- what the call said -- */

interface Proposed<T> { value: T; quote: string; at: number }
interface CallReading {
  id: string;
  source: string;
  status: string;
  createdAt: string;
  redactionCounts: Record<string, number>;
  proposed: {
    outcome: Proposed<string>;
    carrier: Proposed<string> | null;
    premium: Proposed<{ cents: number; mode: string }> | null;
    faceAmountCents: Proposed<number> | null;
    ambiguities: string[];
  };
}

/**
 * The outcomes that can become a case, and the ceiling on what they claim.
 *
 * Mirrors `OUTCOME_STATUS` in packages/core/src/extract.ts, which is what the
 * server actually enforces — this only decides whether to show a button. If the
 * two ever disagree the server refuses and says why, which is the right way
 * round: the screen may be wrong about what is offered, never about what
 * happened.
 */
const OUTCOME_MAKES_A_CASE = new Set(['application_started', 'interested']);

const OUTCOME_TEXT: Record<string, string> = {
  application_started: 'Agreed to apply',
  interested: 'Interested, no application yet',
  callback: 'Asked to be called back',
  not_interested: 'Not interested',
  do_not_call: 'Asked not to be called again',
  unknown: 'Could not tell',
};

/**
 * The one tap between a machine reading a call and the agency believing it.
 *
 * WHY THIS SCREEN SHOWS THE QUOTE
 * A confirmation button with nothing behind it gets pressed without being read,
 * every time, by everybody — and then the tap is not a check, it is a rubber
 * stamp with an audit trail, which is worse than no check because it looks like
 * one. So each field carries the words it came from. An agent can tell in two
 * seconds whether "$47.30 a month" was the premium or the other quote, without
 * opening the transcript.
 *
 * And where the reading could not decide, it says so rather than guessing. A
 * call that priced two coverage levels produces no premium and a line saying
 * which two were mentioned, because a figure that is wrong one time in five is
 * worse than a blank: a blank prompts somebody, a wrong number does not.
 */
async function callReadings(leadId: string, host: HTMLElement): Promise<void> {
  clear(host);
  let items: CallReading[] = [];
  try {
    items = (await get<{ items: CallReading[] }>(`/v1/leads/${leadId}/call-readings`)).items;
  } catch {
    // The panel is an extra, not the screen. A lead still opens if this fails.
    return;
  }
  const pending = items.filter((x) => x.status === 'pending');
  if (pending.length === 0) {
    host.append(pasteBox(leadId, host));
    return;
  }

  for (const r of pending) {
    const p = r.proposed;
    const rows = el('div', { class: 'rows' });

    const line = (label: string, value: string | null, quote: string): void => {
      rows.append(row({
        title: label,
        sub: value === null ? 'Not clear from the call' : quote,
        right: value === null ? muted('—') : el('b', { text: value }),
      }));
    };

    line('Outcome', OUTCOME_TEXT[p.outcome.value] ?? p.outcome.value, p.outcome.quote);
    line('Carrier', p.carrier?.value ?? null, p.carrier?.quote ?? '');
    line(
      'Premium',
      p.premium ? `${fmtMoney(p.premium.value.cents)} ${p.premium.value.mode}` : null,
      p.premium?.quote ?? '',
    );
    line(
      'Coverage',
      p.faceAmountCents ? fmtMoney(p.faceAmountCents.value) : null,
      p.faceAmountCents?.quote ?? '',
    );

    const kids: Node[] = [
      el('div', { class: 'note', style: 'margin-bottom:12px' },
        el('b', null, 'Read off the call. Nothing has been saved to a case. '),
        'Check it against what you remember, then confirm — or say it got it wrong.'),
      rows,
    ];

    for (const a of p.ambiguities) {
      kids.push(el('div', { class: 'warnbox', style: 'margin-top:10px' },
        el('b', null, 'Could not tell: '), a));
    }

    const removed = Object.values(r.redactionCounts).reduce((n, x) => n + x, 0);
    if (removed > 0) {
      kids.push(el('div', { style: 'margin-top:10px;font-size:11.5px;color:var(--ink-4)' },
        `${removed} account or social security number${removed === 1 ? '' : 's'} `
        + 'were removed from this transcript before it was stored.'));
    }

    /*
     * Three buttons, and the third is a separate decision on purpose.
     *
     * "That is right" says the reading matches the call. "Open a case from
     * this" says the agency is claiming a piece of business — a number that
     * lands in production, in placement rate, and eventually in somebody's
     * commission. Folding the second into the first would make a button
     * labelled "that is right" quietly write a policy row, which is the exact
     * thing this whole flow is built to avoid.
     *
     * The case button appears only once the reading has been confirmed, so the
     * order cannot be got wrong by clicking the wrong thing first.
     */
    const yes = button('That is right', 'pri');
    const no = button('It got it wrong', '');
    const row3 = el('div', { style: 'display:flex;gap:8px;margin-top:14px' }, yes, no);

    const openCase = async (btn: HTMLButtonElement): Promise<void> => {
      btn.disabled = true;
      btn.textContent = 'Opening…';
      try {
        await post(`/v1/call-readings/${r.id}/case`);
        toast('Case opened, as submitted to the carrier.', 'ok');
        void callReadings(leadId, host);
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not open a case.', 'crit');
        btn.disabled = false;
        btn.textContent = 'Open a case from this';
      }
    };

    const decide = async (accept: boolean): Promise<void> => {
      yes.disabled = true; no.disabled = true;
      try {
        await post(`/v1/call-readings/${r.id}/decide`, { accept });
        if (!accept) {
          toast('Marked as wrong — nothing was saved.', 'ok');
          void callReadings(leadId, host);
          return;
        }
        // Confirmed. Now offer the paperwork, if the call supports any.
        clear(row3);
        if (OUTCOME_MAKES_A_CASE.has(p.outcome.value)) {
          const make = button('Open a case from this', 'pri');
          make.addEventListener('click', () => { void openCase(make); });
          row3.append(
            el('div', { class: 'okbox', style: 'margin-bottom:10px;width:100%' },
              el('b', null, 'Confirmed. '),
              'Nothing is in the book of business yet — that is the next button, '
              + 'and it opens the case as submitted to the carrier, not in force.'),
            make,
          );
        } else {
          row3.append(el('div', { class: 'okbox' }, el('b', null, 'Confirmed. '),
            'No application came out of this call, so there is nothing to open.'));
        }
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not save that.', 'crit');
        yes.disabled = false; no.disabled = false;
      }
    };
    yes.addEventListener('click', () => { void decide(true); });
    no.addEventListener('click', () => { void decide(false); });
    kids.push(row3);

      host.append(panel('What this call sounded like', ...kids));
  }
  /*
   * The way in to the full history.
   *
   * The panel above shows readings waiting for a decision. Everything else this
   * household's calls produced — the state log, the disclosure, the recording
   * reference, every promise made — lives on `/leads/<id>/calls`, and until
   * this link existed there was no route to it from anywhere in the product.
   */
  host.append(el('div', { style: 'margin:-4px 0 14px' },
    button('Every call on this household', 'sm', () => {
      window.location.hash = `#/leads/${leadId}/calls`;
    })));
  host.append(pasteBox(leadId, host));
}

/**
 * Getting the words in, with nothing installed anywhere.
 *
 * WHY THIS EXISTS AND IS NOT A STOPGAP
 * Everything downstream of a transcript was built first, and correctly: the
 * identifiers come out, the carrier and premium and outcome are read, a person
 * confirms before anything reaches a case. But it all sat idle, because the
 * only way to produce a transcript was a folder watcher that somebody has to
 * install on a Mac and keep running.
 *
 * A box an agent can paste into needs no install, no vendor, no key and no
 * per-minute charge, and it takes a transcript from wherever one already
 * exists — a phone's own voice-memo transcription, a video call's transcript,
 * or an agent typing three sentences from memory while the call is fresh.
 * Three sentences is still less work than six fields, and it is available to
 * every agent on every device today rather than to whoever set up the watcher.
 *
 * The text goes up raw and the server strips the identifiers, exactly as it
 * does for the watcher. There is one copy of that rule and this is not it.
 */
function pasteBox(leadId: string, host: HTMLElement): HTMLElement {
  const box = el('textarea', {
    rows: '5',
    placeholder: 'Paste a transcript, or type what was said — the carrier, the premium, '
      + 'and whether they agreed to apply.',
    style: 'width:100%;font:inherit;padding:9px 11px;border-radius:9px;'
      + 'border:1px solid var(--line);background:var(--bg-2);color:var(--ink-1);resize:vertical',
  });

  const go = button('Read this call', 'pri');
  go.addEventListener('click', () => {
    const text = box.value.trim();
    if (!text) { toast('Nothing to read yet.', 'crit'); return; }
    go.disabled = true;
    go.textContent = 'Reading…';
    void (async () => {
      try {
        await post(`/v1/leads/${leadId}/transcript`, { transcript: text, source: 'typed' });
        box.value = '';
        void callReadings(leadId, host);
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not read that.', 'crit');
      } finally {
        go.disabled = false;
        go.textContent = 'Read this call';
      }
    })();
  });

  return panel('Read a call',
    el('div', { class: 'note', style: 'margin-bottom:12px' },
      el('b', null, 'Account and social security numbers are removed before this is stored. '),
      'Paste it as it was said — you do not need to clean it up first.'),
    box,
    el('div', { style: 'margin-top:12px' }, go));
}


/* ------------------------------------------------------- one lead by hand -- */

/**
 * Typing in a single lead.
 *
 * WHY THIS WAS MISSING FOR SO LONG, AND WHY THAT MATTERS
 * `POST /v1/leads` had thirteen passing tests and no button anywhere in the
 * product. The only way a person could enter the system was a CSV import — so
 * an agent taking a referral on the phone ("my sister needs coverage, here is
 * her number") had nowhere to put it, and would have had to build a
 * spreadsheet to record one name.
 *
 * A route with no caller passes every test that looks at the route, which is
 * exactly the failure `cases.wiring.test.ts` was written for and exactly the
 * one it kept happening beside.
 *
 * The phone is the only required field, and deliberately so: it is the one the
 * whole platform is keyed on — deduplication, the compliance gate, the
 * do-not-call check, the transcriber matching a recording to a person. A lead
 * with a name and no number is a note, not a lead.
 */
/**
 * Writing down why this person may lawfully be called.
 *
 * WHY THIS SCREEN EXISTS
 * Consent could only enter this system through a CSV import from a lead vendor.
 * So a referral who rang the agency and asked for a call back could be typed in
 * as a lead and then never dialled — there was nowhere to record why calling
 * them was permitted — while a purchased list of strangers could be dialled all
 * day. The permission an agent obtains themselves is the strongest and cheapest
 * there is, and it was the one kind the platform could not hold.
 *
 * WHY IT ASKS FOR SO MUCH
 * Every field here is what somebody needs a year from now, when a call is
 * questioned and nobody remembers it. "They said yes" is not a record. What
 * they actually agreed to, when, and how it was obtained, is.
 *
 * The grade is not offered as a choice. A signed form and a remembered
 * conversation are both real consent and they are not the same evidence, and an
 * agent who could pick would have an agency full of A grades inside a week.
 */
function consentPanel(leadId: string, lead: Lead, onSaved: () => void): HTMLElement {
  const host = el('div');

  const method = select([
    ['verbal', 'They told me directly'],
    ['inbound', 'They contacted us first'],
    ['written', 'They wrote to us — email, text or on paper'],
    ['web_form', 'They filled in a form online'],
  ], 'verbal');
  const when = input({ type: 'date', value: new Date().toISOString().slice(0, 10) });
  const said = el('textarea', {
    class: 'inp', rows: '3',
    placeholder: 'Asked me to call her back Thursday about final expense cover for her and her husband.',
  }) as HTMLTextAreaElement;
  const formUrl = input({ placeholder: 'https://…', type: 'url' });
  const urlField = field('Where the form was', formUrl,
    'The page they filled in. Without it there is nothing to go back and look at.');
  urlField.style.display = 'none';
  method.addEventListener('change', () => {
    urlField.style.display = method.value === 'web_form' ? '' : 'none';
  });

  const voice = input({ type: 'checkbox' });
  voice.checked = true;
  const sms = input({ type: 'checkbox' });
  const email = input({ type: 'checkbox' });
  const channels = el('div', { style: 'display:flex;gap:16px;flex-wrap:wrap;margin-bottom:14px' },
    el('label', { style: 'display:flex;gap:6px;align-items:center' }, voice, 'Calls'),
    el('label', { style: 'display:flex;gap:6px;align-items:center' }, sms, 'Texts'),
    el('label', { style: 'display:flex;gap:6px;align-items:center' }, email, 'Email'));

  const save = button('Record it', 'pri');
  save.addEventListener('click', () => {
    const picked = [
      voice.checked ? 'voice' : null,
      sms.checked ? 'sms' : null,
      email.checked ? 'email' : null,
    ].filter(Boolean) as string[];
    if (!picked.length) { toast('Say which channels they agreed to.', 'crit'); return; }
    if (said.value.trim().length < 10) {
      toast('Write down what they actually agreed to — it is the whole record.', 'crit');
      said.focus();
      return;
    }
    save.disabled = true;
    void (async () => {
      try {
        await post(`/v1/leads/${leadId}/consent`, {
          method: method.value,
          capturedAt: new Date(`${when.value}T12:00:00Z`).toISOString(),
          languageText: said.value.trim(),
          channels: picked,
          ...(method.value === 'web_form' && formUrl.value ? { formUrl: formUrl.value } : {}),
        });
        toast('Recorded.', 'ok');
        onSaved();
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit');
        save.disabled = false;
      }
    })();
  });

  host.append(panel('Permission to call',
    el('div', { class: 'note', style: 'margin-bottom:14px' },
      el('b', null, 'Only if it actually happened. '),
      'This opens the compliance gate for this person, and the record says who wrote it. '
      + 'It is for permission somebody genuinely gave — not for getting a number past a check.'),
    field('How they gave it', method),
    field('When', when),
    said ? field('What they agreed to, in their words', said) : null,
    urlField,
    el('label', { class: 'field' }, el('span', { text: 'What it covers' }), channels),
    save));

  void lead;
  return host;
}

function addLeadSheet(onSaved: () => void): void {
  const firstName = input({ placeholder: 'Jane' });
  const lastName = input({ placeholder: 'Miller' });
  const phone = input({ type: 'tel', placeholder: '(313) 555-1234', required: true });
  const email = input({ type: 'email', placeholder: 'optional' });
  const st = select([['', 'Not known'], ...STATES.map((x) => [x.code, x.name] as [string, string])]);
  const zip = input({ placeholder: 'optional' });
  const dob = input({ type: 'date' });
  const notes = el('textarea', {
    rows: '3', placeholder: 'Where they came from, what they asked for.',
    style: 'width:100%;font:inherit;padding:9px 11px;border-radius:9px;'
      + 'border:1px solid var(--line);background:var(--bg-2);color:var(--ink-1);resize:vertical',
  });

  const save = button('Add the lead', 'pri');
  const body = el('div', null,
    el('div', { class: 'note', style: 'margin-bottom:14px' },
      el('b', null, 'The phone number is the only thing required. '),
      'It is what the duplicate check, the calling rules and the do-not-call check all '
      + 'work from, so a record without one cannot be called or checked.'),
    el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
      field('First name', firstName),
      field('Last name', lastName)),
    field('Phone', phone),
    field('Email', email),
    el('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:10px' },
      field('State', st, 'Sets the calling window and checks your licence.'),
      field('ZIP', zip)),
    field('Date of birth', dob, 'Used for quoting and for the draft-date advice.'),
    field('Notes', notes),
    el('div', { style: 'margin-top:6px' }, save));

  const close = drawer('Add a lead', body);

  save.addEventListener('click', () => {
    const digits = phone.value.replace(/\D/g, '');
    if (digits.length < 10) {
      toast('That phone number is too short to be dialled.', 'crit');
      phone.focus();
      return;
    }
    save.disabled = true;
    save.textContent = 'Adding…';
    void (async () => {
      try {
        // Only what was filled in. Sending empty strings would store blanks
        // that read as "known to be nothing" rather than "never asked".
        const payload: Record<string, string> = { phone: phone.value.trim() };
        for (const [key, ctl] of [
          ['firstName', firstName], ['lastName', lastName], ['email', email],
          ['zip', zip], ['dob', dob], ['notes', notes],
        ] as Array<[string, HTMLInputElement | HTMLTextAreaElement]>) {
          const v2 = ctl.value.trim();
          if (v2) payload[key] = v2;
        }
        if (st.value) payload.state = st.value;

        const made = await post<{ id: string }>('/v1/leads', payload);
        toast('Lead added.', 'ok');
        close();
        onSaved();
        return made;
      } catch (e) {
        toast(e instanceof ApiError ? e.message : 'Could not add that lead.', 'crit');
        save.disabled = false;
        save.textContent = 'Add the lead';
        return null;
      }
    })();
  });
}
